/**
 * RunSelector — choose which Scan_Run the dashboard is viewing (Req 1).
 *
 * Responsibilities (design "React SPA components", Property 1):
 *  - Order the available runs by the same rule the aggregator uses: run-date
 *    descending, then run-id descending (Req 1.1, 1.2). The catalog written to
 *    `dashboard/runs.json` is already ordered, but we re-apply the rule
 *    defensively so the UI is correct even if the catalog is stale or hand-edited.
 *  - Default the selection to the newest run — the first entry after ordering
 *    (Req 1.2).
 *  - Label each entry with its run-date and run-id (Req 1.1).
 *  - Show the currently active run's run-date and run-id in a header (Req 1.6).
 *  - Render a "no scan results available" message when the catalog is empty
 *    (Req 1.7).
 *
 * This component is presentational: it takes the catalog `runs`, the selected
 * run key, and an `onSelect` callback as props so it is trivially testable. The
 * fetching of `dashboard/runs.json` is provided by the {@link fetchRunsCatalog}
 * helper exported here and wired in by the App (task 11.2), keeping I/O out of
 * the render path.
 */

import { useEffect } from "react";
import type { RunCatalogEntry, RunsCatalog } from "../types";

/** Message shown when there are no runs to select (Req 1.7). */
export const NO_RUNS_MESSAGE = "No scan results available.";

/**
 * Default location of the runs catalog served behind CloudFront. Root-absolute
 * (leading slash) so it resolves against the origin regardless of the current
 * client route and maps to the CloudFront `dashboard/*` behavior.
 */
export const RUNS_CATALOG_PATH = "/dashboard/runs.json";

/**
 * Stable identity for a run, used as the selection key and the basis for the
 * deep-link route `/run/:runDate/:runId`. The (run-date, run-id) pair uniquely
 * identifies a Scan_Run (requirements Glossary).
 */
export function runKey(entry: Pick<RunCatalogEntry, "runDate" | "runId">): string {
  return `${entry.runDate}/${entry.runId}`;
}

/**
 * Order runs most-recent-first: run-date descending, then run-id descending
 * (Req 1.1, 1.2). This mirrors the aggregator's `order_runs` (Property 1).
 *
 * Returns a new array; the input is not mutated. `runDate` is `YYYY-MM-DD` so
 * lexicographic comparison matches chronological order; `runId` ties are broken
 * lexicographically descending.
 */
export function orderRuns(runs: RunCatalogEntry[]): RunCatalogEntry[] {
  return [...runs].sort((a, b) => {
    if (a.runDate !== b.runDate) {
      return a.runDate < b.runDate ? 1 : -1; // run-date descending
    }
    if (a.runId !== b.runId) {
      return a.runId < b.runId ? 1 : -1; // run-id descending
    }
    return 0;
  });
}

/**
 * Fetch and parse the runs catalog (`dashboard/runs.json`).
 *
 * Thin I/O helper kept out of the render path so the component stays pure and
 * testable. The caller (App / a small wrapper) owns when to fetch and how to
 * handle errors; a rejected promise should surface the catalog-fetch-failure
 * path (Req 1.7 empty / Error Handling "runs catalog fetch fails").
 *
 * @param signal - Optional AbortSignal to cancel the request.
 * @param basePath - Optional override for the catalog path (defaults to
 *   {@link RUNS_CATALOG_PATH}).
 * @param fetchFn - Fetch implementation to use; defaults to the global
 *   `fetch`. Injecting a token-attaching `authedFetch` here routes the runs
 *   catalog request through the same authorized fetch wrapper as the other
 *   data fetch points (Req 3.2), while preserving the current behavior when
 *   not provided.
 */
export async function fetchRunsCatalog(
  signal?: AbortSignal,
  basePath: string = RUNS_CATALOG_PATH,
  fetchFn: typeof fetch = fetch,
): Promise<RunsCatalog> {
  const response = await fetchFn(basePath, { signal });
  if (!response.ok) {
    throw new Error(
      `Failed to load runs catalog (${response.status} ${response.statusText})`,
    );
  }
  return (await response.json()) as RunsCatalog;
}

/** Human-readable label for an option/header: "<run-date> · <run-id>" (Req 1.1, 1.6). */
export function runLabel(entry: RunCatalogEntry): string {
  return `${entry.runDate} \u00b7 ${entry.runId}`;
}

export interface RunSelectorProps {
  /** Available runs from the catalog. May be in any order; re-ordered here. */
  runs: RunCatalogEntry[];
  /** The currently selected run key (`runDate/runId`), or `null` if none yet. */
  selectedRunKey: string | null;
  /** Called with a run key when the user picks a run, or to apply the default. */
  onSelect: (runKey: string) => void;
}

/**
 * Run picker plus the active-run header. When the catalog is empty it renders
 * the no-results message (Req 1.7); otherwise it orders the runs, defaults the
 * selection to the newest run when none is selected (Req 1.2), and shows the
 * active run's run-date and run-id (Req 1.6).
 */
export default function RunSelector({
  runs,
  selectedRunKey,
  onSelect,
}: RunSelectorProps) {
  const ordered = orderRuns(runs);

  // Default to the newest run (first after ordering) when nothing is selected
  // yet (Req 1.2). Done in an effect so selection state stays owned by the
  // parent and the default is applied exactly once per catalog/selection change.
  const defaultKey = ordered.length > 0 ? runKey(ordered[0]) : null;
  useEffect(() => {
    if (selectedRunKey === null && defaultKey !== null) {
      onSelect(defaultKey);
    }
  }, [selectedRunKey, defaultKey, onSelect]);

  // No runs available (Req 1.7).
  if (ordered.length === 0) {
    return (
      <section aria-label="Scan run selector" className="ds-panel">
        <p role="status" className="ds-active-run">
          {NO_RUNS_MESSAGE}
        </p>
      </section>
    );
  }

  // The active run is the selected one, falling back to the newest default so
  // the header always reflects a concrete run (Req 1.6).
  const activeKey = selectedRunKey ?? defaultKey;
  const activeRun =
    ordered.find((entry) => runKey(entry) === activeKey) ?? ordered[0];

  return (
    <section aria-label="Scan run selector" className="ds-panel">
      <header className="ds-panel__header">
        <h2 className="ds-panel__title">Scan run</h2>
        {/* Active run-date and run-id (Req 1.6). */}
        <p className="ds-active-run">
          <span data-testid="active-run-date">{activeRun.runDate}</span>
          {" \u00b7 "}
          <span data-testid="active-run-id">{activeRun.runId}</span>
        </p>
      </header>

      <div className="ds-field">
        <label htmlFor="run-selector" className="ds-label">
          Select a scan run
        </label>
        <select
          id="run-selector"
          className="ds-select"
          value={runKey(activeRun)}
          onChange={(event) => onSelect(event.target.value)}
        >
          {/* Each entry labeled with run-date and run-id (Req 1.1). */}
          {ordered.map((entry) => {
            const key = runKey(entry);
            return (
              <option key={key} value={key}>
                {runLabel(entry)}
              </option>
            );
          })}
        </select>
      </div>
    </section>
  );
}
